import React, { useRef, useEffect, useState } from 'react';
import { Promotion, LayoutType, LayoutStyle, TextStyle, PanfletoElementPlacements, PanfletoFieldStyle, PanfletoStyleFieldKey } from '../../../shared/types';
import {
  getPanfletoBackgroundCssColor,
  mergePanfletoFieldStyle,
  mergePanfletoContent,
  mergePanfletoElementPlacements,
  getDefaultPanfletoElementPlacements,
  panfletoHasExplicitBackgroundColor,
  panfletoHasFreeformPlacements,
} from '../../banners/utils/panfletoContentUtils';
import { PanfletoFlyerContent } from './PanfletoFlyerContent';
import { bannerService } from '../../banners/services/bannerService';
import {
  getPanfletoFotoDisplayUrlFromCache,
  putPanfletoFotoBlobInCache,
} from '../../banners/utils/panfletoProdutoFotoBlobCache';
import { Video } from 'lucide-react';
import { useSimulationFrame } from '../../display/context/SimulationFrameContext';

interface PromoCardProps {
  promo: Promotion;
  layout: LayoutType;
  /** Mesmo que colunas: vem de `configuracoes` → `layoutStyleFromTelaConfiguracoes` / `jsonToLayoutStyle` (inclui `panfletoContent`). */
  layoutStyle?: LayoutStyle;
  /**
   * Quando true (apenas Display / Televisão), foto por `promo.id` no panfleto (GET na 1ª vez;
   * depois reutiliza cache em memória na rotação). Não usar no editor de estilo.
   */
  panfletoProdutoFotoViaApi?: boolean;
  /** Modo “Canva” na página de display: arrastar blocos (rascunho controlado pelo pai). */
  panfletoPositionEditMode?: boolean;
  panfletoPlacementDraft?: PanfletoElementPlacements | null;
  onPanfletoPlacementDraftChange?: (next: PanfletoElementPlacements) => void;
  onPanfletoFieldStyleDraftChange?: (
    field: PanfletoStyleFieldKey,
    patch: Partial<PanfletoFieldStyle>,
    options?: { replace?: boolean }
  ) => void;
  /** Tela do panfleto para buscar foto de fundo dedicada em /v1/banner/tela/{id}/foto. */
  panfletoTelaId?: number;
  /**
   * Incrementado pelo pai após TelaAtualizada / merge de tela — obriga novo GET da foto de fundo.
   * Não usar `layoutStyle` como dependência: mudanças só em panfletoContent (ex.: cor) cancelavam o fetch
   * e a URL em cache podia ficar revogada ou desalinhada do servidor.
   */
  panfletoTelaFotoReloadToken?: number;
  /** Panfleto: se false, não usa layout com borda compartilhada (ex.: tela sem produtos no display). */
  panfletoHasProductsOnScreen?: boolean;
}

export const PromoCard: React.FC<PromoCardProps> = ({
  promo,
  layout,
  layoutStyle,
  panfletoProdutoFotoViaApi = false,
  panfletoPositionEditMode = false,
  panfletoPlacementDraft = null,
  onPanfletoPlacementDraftChange,
  onPanfletoFieldStyleDraftChange,
  panfletoTelaId,
  panfletoTelaFotoReloadToken = 0,
  panfletoHasProductsOnScreen,
}) => {
  const calculateDiscount = (original: number, current: number) => {
    if (!original || !current) return 0;
    return Math.round(((original - current) / original) * 100);
  };

  const discount = promo.originalPrice
    ? calculateDiscount(promo.originalPrice, promo.price)
    : 0;

  const [panfletoFotoObjectUrl, setPanfletoFotoObjectUrl] = useState<string | null>(null);
  const [panfletoTelaFotoBg, setPanfletoTelaFotoBg] = useState<string | null>(null);
  const inSimulationFrame = useSimulationFrame();
  /** Incrementa a cada troca de produto no panfleto — descarta respostas HTTP atrasadas. */
  const panfletoFotoFetchGen = useRef(0);

  useEffect(() => {
    if (layout !== 'panfleto' || !panfletoProdutoFotoViaApi || !promo?.id) {
      setPanfletoFotoObjectUrl(null);
      return;
    }

    const produtoId = promo.id;
    const fetchGen = ++panfletoFotoFetchGen.current;
    let cancelled = false;

    const cachedUrl = getPanfletoFotoDisplayUrlFromCache(produtoId);
    if (cachedUrl) {
      setPanfletoFotoObjectUrl(cachedUrl);
    } else {
      setPanfletoFotoObjectUrl(null);

      const load = async () => {
        try {
          const blob = await bannerService.buscarFotoProduto(produtoId);
          if (cancelled) return;
          if (!blob || blob.size === 0) {
            if (panfletoFotoFetchGen.current === fetchGen) {
              setPanfletoFotoObjectUrl(null);
            }
            return;
          }
          if (panfletoFotoFetchGen.current !== fetchGen) {
            putPanfletoFotoBlobInCache(produtoId, blob);
            return;
          }
          const url = putPanfletoFotoBlobInCache(produtoId, blob);
          if (url) setPanfletoFotoObjectUrl(url);
        } catch {
          if (!cancelled && panfletoFotoFetchGen.current === fetchGen) {
            setPanfletoFotoObjectUrl(null);
          }
        }
      };

      void load();
    }

    return () => {
      cancelled = true;
      setPanfletoFotoObjectUrl(null);
    };
  }, [layout, panfletoProdutoFotoViaApi, promo.id]);

  useEffect(() => {
    let cancelled = false;
    if (layout !== 'panfleto' || !panfletoTelaId) {
      setPanfletoTelaFotoBg(null);
      return;
    }

    (async () => {
      const foto = await bannerService.buscarFotoTela(panfletoTelaId);
      if (!cancelled) {
        setPanfletoTelaFotoBg(foto);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [layout, panfletoTelaId, panfletoTelaFotoReloadToken]);

  // Função para formatar números no padrão brasileiro
  const formatBrazilianNumber = (value: number | undefined, showCurrency: boolean = true): string => {
    // Garantir que o valor seja um número válido
    const numValue = value ?? 0;
    const formattedNumber = numValue.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });

    return showCurrency ? `R$ ${formattedNumber}` : formattedNumber;
  };

  // Função auxiliar para converter hex para rgba
  const hexToRgba = (hex: string, alpha: number): string => {
    let cleanHex = hex.replace('#', '');
    // Expandir hex de 3 para 6 caracteres se necessário
    if (cleanHex.length === 3) {
      cleanHex = cleanHex.split('').map(char => char + char).join('');
    }
    if (cleanHex.length === 6) {
      const r = parseInt(cleanHex.substr(0, 2), 16);
      const g = parseInt(cleanHex.substr(2, 2), 16);
      const b = parseInt(cleanHex.substr(4, 2), 16);
      if (!isNaN(r) && !isNaN(g) && !isNaN(b)) {
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
      }
    }
    return hex; // Retornar original se não conseguir converter
  };

  // Apply container styling if available
  const getContainerStyle = () => {
    if (!layoutStyle || !layoutStyle.productContainer) return {};

    const containerStyle = layoutStyle.productContainer;

    // Aplicar opacidade e blur no background do card (não na imagem de fundo)
    const opacity = containerStyle.backgroundImageOpacity !== undefined 
      ? containerStyle.backgroundImageOpacity / 100 
      : 1;
    const blur = containerStyle.backgroundImageBlur ?? 0;

    let backgroundStyle: React.CSSProperties = {};
    
    // Aplicar opacidade diretamente nas cores do gradiente ou background
    if (containerStyle.useGradient && containerStyle.gradientStart && containerStyle.gradientEnd) {
      // Para gradiente, aplicar opacidade nas cores do gradiente
      const startColor = opacity < 1 
        ? hexToRgba(containerStyle.gradientStart, opacity)
        : containerStyle.gradientStart;
      const endColor = opacity < 1
        ? hexToRgba(containerStyle.gradientEnd, opacity)
        : containerStyle.gradientEnd;
      backgroundStyle.background = `linear-gradient(to right, ${startColor}, ${endColor})`;
    } else if (containerStyle.backgroundColor) {
      // Para background color sólido, aplicar opacidade via rgba
      if (opacity < 1) {
        backgroundStyle.backgroundColor = hexToRgba(containerStyle.backgroundColor, opacity);
      } else {
        backgroundStyle.backgroundColor = containerStyle.backgroundColor;
      }
    }

    return {
      ...backgroundStyle,
      borderRadius: containerStyle.borderRadius ? `${containerStyle.borderRadius}px` : undefined,
      padding: `${containerStyle.paddingTop || 0}px ${containerStyle.paddingRight || 0}px ${containerStyle.paddingBottom || 0}px ${containerStyle.paddingLeft || 0}px`,
      margin: `${containerStyle.marginTop || 0}px ${containerStyle.marginRight || 0}px ${containerStyle.marginBottom || 0}px ${containerStyle.marginLeft || 0}px`,
      // Aplicar blur apenas se > 0 (blur no background do card)
      backdropFilter: blur > 0 ? `blur(${blur}px)` : undefined,
      // Garantir que o background cubra completamente as bordas arredondadas
      backgroundClip: 'border-box',
      overflow: 'hidden',
      // Garantir que não haja borda visível que mostre a forma retangular
      border: 'none',
      outline: 'none',
    };
  };

  // Get text style with defaults
  const getTextStyle = (styleKey: keyof Omit<LayoutStyle, 'productContainer' | 'priceBox'>) => {
    if (!layoutStyle || !layoutStyle[styleKey]) return {};

    const style = layoutStyle[styleKey] as TextStyle;
    const verticalStretchValue = style.verticalStretch || 100;
    const hasVerticalStretch = verticalStretchValue > 100;
    
    // Para unidade, não aplicar transform aqui se houver verticalOffset (será aplicado separadamente)
    const isUnit = styleKey === 'unit';
    const hasVerticalOffset = isUnit && (style.verticalOffset !== undefined && style.verticalOffset !== 0);

    return {
      color: style.color || '#000000',
      fontSize: `${style.fontSize || 16}px`,
      fontFamily: style.fontFamily || 'Arial, sans-serif',
      lineHeight: style.lineHeight || 1.2,
      letterSpacing: style.letterSpacing ? `${style.letterSpacing}px` : 'normal',
      fontWeight: style.fontWeight || 400,
      // Transform no texto do preço precisa de inline-block para renderizar corretamente
      display: (styleKey === 'price' || hasVerticalStretch) ? 'inline-block' : undefined,
      // Não aplicar transform se for unidade com verticalOffset (será aplicado separadamente)
      transform: (hasVerticalOffset ? undefined : (hasVerticalStretch
        ? `scaleY(${verticalStretchValue / 100})`
        : undefined)),
      transformOrigin: (hasVerticalStretch || hasVerticalOffset) ? 'center' : undefined,
    };
  };

  // Hooks para truncamento do nome (devem estar no topo, antes de qualquer retorno condicional)
  const containerRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLHeadingElement>(null);
  const unitRef = useRef<HTMLSpanElement>(null);
  const [truncatedName, setTruncatedName] = useState(promo.name);

  // Effect para truncar texto com apenas um ponto (apenas para layout simple2col)
  useEffect(() => {
    // Só executar para layout simple2col
    if (layout !== 'simple2col') {
      setTruncatedName(promo.name);
      return;
    }

    const containerElement = containerRef.current;
    const nameElement = nameRef.current;
    const unitElement = unitRef.current;
    
    if (!containerElement || !nameElement || !unitElement) {
      // Se os elementos ainda não estão disponíveis, manter o nome original
      setTruncatedName(promo.name);
      return;
    }

    const checkTruncation = () => {
      // Criar um elemento temporário invisível para medir sem afetar o DOM visível
      const tempElement = document.createElement('span');
      tempElement.style.visibility = 'hidden';
      tempElement.style.position = 'absolute';
      tempElement.style.whiteSpace = 'nowrap';
      tempElement.style.fontSize = window.getComputedStyle(nameElement).fontSize;
      tempElement.style.fontFamily = window.getComputedStyle(nameElement).fontFamily;
      tempElement.style.fontWeight = window.getComputedStyle(nameElement).fontWeight;
      tempElement.style.letterSpacing = window.getComputedStyle(nameElement).letterSpacing;
      document.body.appendChild(tempElement);
      
      // Obter a largura disponível do container pai
      const containerWidth = containerElement.clientWidth;
      
      // Obter a largura ocupada pela unidade (incluindo margin)
      const unitWidth = unitElement.offsetWidth;
      const unitMargin = parseInt(window.getComputedStyle(unitElement).marginLeft) || 0;
      const totalUnitWidth = unitWidth + unitMargin;
      
      // Calcular o espaço disponível para o nome (container - unidade)
      const availableWidth = containerWidth - totalUnitWidth;
      
      // Testar se o texto original cabe
      tempElement.textContent = promo.name;
      const fullTextWidth = tempElement.offsetWidth;
      
      if (fullTextWidth > availableWidth) {
        // Precisa truncar - encontrar o tamanho correto
        let text = promo.name;
        while (text.length > 0) {
          const testText = text + '.';
          tempElement.textContent = testText;
          if (tempElement.offsetWidth <= availableWidth) {
            setTruncatedName(testText);
            document.body.removeChild(tempElement);
            return;
          }
          text = text.slice(0, -1);
        }
        setTruncatedName('.');
      } else {
        setTruncatedName(promo.name);
      }
      
      document.body.removeChild(tempElement);
    };

    // Usar requestAnimationFrame para garantir que o DOM esteja renderizado, mas de forma mais rápida
    const rafId = requestAnimationFrame(() => {
      checkTruncation();
    });
    
    // Verificar quando a janela redimensionar
    window.addEventListener('resize', checkTruncation);
    
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', checkTruncation);
    };
  }, [layout, promo.name, promo.unit]);

  // Video promotion card
  if (promo.isVideoPromotion) {
    // Determinar se deve usar o borderRadius da classe ou do estilo
    const useDefaultBorderRadius = !layoutStyle || !layoutStyle.productContainer || layoutStyle.productContainer.borderRadius === undefined;

    const cardStyles: Record<LayoutType, string> = {
      simple2col: `bg-white ${useDefaultBorderRadius ? 'rounded-lg' : ''} shadow-sm px-6 py-4 hover:shadow-md transition-all relative`,
      panfleto: `bg-white ${useDefaultBorderRadius ? 'rounded-2xl' : ''} shadow-xl p-8 flex flex-col items-center justify-center w-full max-w-6xl mx-auto relative`,
      video: `bg-white ${useDefaultBorderRadius ? 'rounded-xl' : ''} shadow-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 relative w-full`
    };

    const containerStyle = getContainerStyle();
    const combinedStyle = {
      ...containerStyle
    };

    return (
      <div className={cardStyles[layout]} style={combinedStyle}>
        <div className="flex flex-col">
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-blue-500 text-white p-2 rounded-full">
              <Video size={layout === 'video' ? 18 : 22} />
            </div>
            <h3
              className="font-bold text-xl text-gray-800"
              style={getTextStyle('productName')}
            >
              {promo.name}
            </h3>
          </div>

          <div className="mt-2">
            {promo.video ? (
              <div className="relative pt-[56.25%]">
                <video
                  src={promo.video}
                  className="absolute top-0 left-0 w-full h-full object-cover rounded-lg"
                  controls={false}
                  muted
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 rounded-lg">
                  <div className="bg-white bg-opacity-90 px-3 py-1 rounded-full text-sm font-medium">
                    Vídeo Promocional
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-gray-100 rounded-lg h-24 flex items-center justify-center">
                <span className="text-gray-500">Vídeo Promocional</span>
              </div>
            )}
          </div>

          <div className="mt-3">
            <p
              className="text-sm text-gray-600 mb-1"
              style={layoutStyle?.category ? getTextStyle('category') : {}}
            >
              <span className="font-medium">Categoria:</span> {promo.category}
            </p>
            {promo.description && (
              <p
                className="text-sm text-gray-600 mt-2 line-clamp-2"
                style={layoutStyle?.description ? getTextStyle('description') : {}}
              >
                {promo.description}
              </p>
            )}
            {promo.validUntil ? (
              <p
                className="text-xs text-red-600 mt-2"
                style={layoutStyle?.validUntil ? getTextStyle('validUntil') : {}}
              >
                Válido até: {new Date(promo.validUntil).toLocaleDateString()}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  const containerStyle = getContainerStyle();

  if (layout === 'panfleto') {
    // Palco: cor de fundo + imagem (opacidade/blur). Borda compartilhada usa só as configs dela.
    const useDefaultBorderRadius = !layoutStyle || !layoutStyle.productContainer || layoutStyle.productContainer.borderRadius === undefined;
    const pc = layoutStyle?.productContainer;
    const bgImage = panfletoTelaFotoBg || pc?.backgroundImage;
    const bgVisible = pc?.backgroundImageVisible !== false;
    const bgImageOpacity = pc?.backgroundImageOpacity ?? 100;
    const shouldHideShadow = Boolean(bgImage && bgVisible && bgImageOpacity === 0);
    const panfletoBg = getPanfletoBackgroundCssColor(layoutStyle?.panfletoContent);
    const hasPanfletoCustomBg = panfletoHasExplicitBackgroundColor(layoutStyle?.panfletoContent);
    const hasVisibleBgImage = Boolean(bgImage && bgVisible);
    const blurPx = pc?.backgroundImageBlur ?? 0;
    /** Cor + imagem: cor na base, imagem por cima. Não sobrescreve a borda compartilhada. */
    const panfletoLayeredBg = Boolean(
      hasVisibleBgImage && (hasPanfletoCustomBg || bgImageOpacity < 100 || blurPx > 0)
    );
    const hasStageCustomFill = hasVisibleBgImage || hasPanfletoCustomBg;

    const baseCard: React.CSSProperties = { ...containerStyle };
    delete (baseCard as { backgroundImage?: string }).backgroundImage;
    delete (baseCard as { backgroundImageOpacity?: number }).backgroundImageOpacity;
    delete (baseCard as { backgroundImageBlur?: number }).backgroundImageBlur;
    delete (baseCard as { backgroundImageVisible?: boolean }).backgroundImageVisible;
    if (panfletoLayeredBg) {
      delete (baseCard as { backdropFilter?: string }).backdropFilter;
    }

    const cardStyle: React.CSSProperties = {
      ...baseCard,
      boxShadow: shouldHideShadow ? 'none' : '0 4px 6px rgba(0, 0, 0, 0.1)',
    };
    if (hasStageCustomFill) {
      cardStyle.background = 'none';
      cardStyle.backgroundImage = 'none';
      cardStyle.backgroundColor = 'transparent';
    }

    const stageStyle: React.CSSProperties = {
      minHeight: inSimulationFrame ? '100%' : '80vh',
      width: '100%',
      height: inSimulationFrame ? '100%' : undefined,
      position: 'relative',
    };
    if (panfletoLayeredBg) {
      stageStyle.backgroundColor = 'transparent';
      stageStyle.backgroundImage = undefined;
    } else if (hasPanfletoCustomBg) {
      stageStyle.backgroundColor = panfletoBg;
      stageStyle.backgroundImage = undefined;
    } else {
      stageStyle.backgroundSize = 'cover';
      stageStyle.backgroundPosition = 'center';
      stageStyle.backgroundRepeat = 'no-repeat';
      stageStyle.backgroundColor = panfletoBg;
      if (hasVisibleBgImage) {
        stageStyle.backgroundImage = `url(${String(bgImage)})`;
      }
    }
    const vignetteEnabled = pc?.backgroundVignetteEnabled === true;
    const vignetteIntensity = Math.max(0, Math.min(100, pc?.backgroundVignetteIntensity ?? 35));
    const vignetteMainAlpha = 0.22 + (vignetteIntensity / 100) * 0.78;
    const vignetteOuterAlpha = 0.06 + (vignetteIntensity / 100) * 0.34;
    const vignetteMainRadius = 120 + (vignetteIntensity / 100) * 240;
    const vignetteOuterRadius = 260 + (vignetteIntensity / 100) * 320;

    const savedPc = mergePanfletoContent(layoutStyle?.panfletoContent);
    const hasSavedFreeform = panfletoHasFreeformPlacements(savedPc);
    const hasDraftFreeform = Boolean(panfletoPlacementDraft && Object.keys(panfletoPlacementDraft).length > 0);
    // Se existir rascunho local, ele prevalece mesmo fora do modo (sem contornos),
    // permitindo pré-visualizar o resultado sem "poluição visual".
    const useFreeformLayout = hasDraftFreeform || hasSavedFreeform;
    const rawPlacements: PanfletoElementPlacements =
      (panfletoPlacementDraft && Object.keys(panfletoPlacementDraft).length > 0
        ? panfletoPlacementDraft
        : (savedPc.elementPlacements as PanfletoElementPlacements)) ?? {};
    const effectivePlacements: PanfletoElementPlacements =
      mergePanfletoElementPlacements(getDefaultPanfletoElementPlacements(), rawPlacements) ?? rawPlacements;

    return (
      <div
        className={`promo-panfleto-stage box-border w-full h-full flex flex-1 flex-col ${
          inSimulationFrame ? 'min-h-0' : 'min-h-[80vh]'
        } ${
          hasStageCustomFill ? 'p-0' : 'p-4 sm:p-8'
        } relative items-stretch`}
        style={stageStyle}
        data-layout="panfleto"
      >
        {panfletoLayeredBg ? (
          <>
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 z-0"
              style={{ backgroundColor: panfletoBg }}
            />
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 z-0"
              style={{
                backgroundImage: `url(${String(bgImage)})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                opacity: bgImageOpacity / 100,
                filter: blurPx > 0 ? `blur(${blurPx}px)` : undefined,
              }}
            />
          </>
        ) : null}
        {vignetteEnabled ? (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-[1]"
            style={{
              boxShadow: `inset 0 0 ${vignetteMainRadius.toFixed(0)}px rgba(0,0,0,${vignetteMainAlpha.toFixed(3)}), inset 0 0 ${vignetteOuterRadius.toFixed(0)}px rgba(0,0,0,${vignetteOuterAlpha.toFixed(3)})`,
            }}
          />
        ) : null}
        <div
          className={`box-border w-full flex flex-col flex-1 ${
            inSimulationFrame || !hasStageCustomFill ? 'min-h-0' : 'min-h-[80vh]'
          } ${shouldHideShadow ? '' : 'shadow-sm'} ${useDefaultBorderRadius ? 'rounded-2xl' : ''} ${
            hasStageCustomFill ? '' : 'bg-white'
          } overflow-hidden`}
          style={cardStyle}
        >
          <PanfletoFlyerContent
            layoutStyle={layoutStyle}
            promo={promo}
            density="display"
            panfletoProdutoFotoViaApi={panfletoProdutoFotoViaApi}
            panfletoFotoObjectUrl={panfletoFotoObjectUrl}
            useFreeformLayout={useFreeformLayout}
            elementPlacements={effectivePlacements}
            positionEditMode={panfletoPositionEditMode}
            onElementPlacementsChange={onPanfletoPlacementDraftChange}
            onFieldStyleChange={onPanfletoFieldStyleDraftChange}
            onInitializeFromCurrentLayout={onPanfletoPlacementDraftChange}
            getTextStyle={getTextStyle}
            panfletoHasProductsOnScreen={panfletoHasProductsOnScreen}
            fixedFrameMode={inSimulationFrame}
          />
        </div>
      </div>
    );
  }

  if (layout === 'simple2col') {
    // Determinar se deve usar o borderRadius da classe ou do estilo
    const useDefaultBorderRadius = !layoutStyle || !layoutStyle.productContainer || layoutStyle.productContainer.borderRadius === undefined;
    const nameStretch = layoutStyle?.productName?.verticalStretch;
    const hasNameVerticalStretch = nameStretch != null && nameStretch > 100;
    
    // Verificar se a opacidade da imagem de fundo é 0% para remover a sombra/borda
    const bgImageOpacity = layoutStyle?.productContainer?.backgroundImageOpacity ?? 100;
    const bgImage = layoutStyle?.productContainer?.backgroundImage;
    const bgVisible = layoutStyle?.productContainer?.backgroundImageVisible !== false;
    const shouldHideShadow = bgImage && bgVisible && bgImageOpacity === 0;

    return (
      <div
        className={`${shouldHideShadow ? '' : 'shadow-sm'} px-3 py-4 ${shouldHideShadow ? '' : 'hover:shadow-md'} transition-all h-full w-full ${useDefaultBorderRadius ? 'rounded-lg' : ''}`}
        style={{
          ...containerStyle,
          // Remover boxShadow quando a opacidade for 0% para que o texto fique integrado na imagem
          boxShadow: shouldHideShadow ? 'none' : '0 4px 6px rgba(0, 0, 0, 0.1)',
        }}
      >
        <div className="flex items-center justify-between w-full h-full">
          <div
            ref={containerRef}
            className={`flex ${layoutStyle?.unit?.verticalAlignment === 'bottom' ? 'items-baseline' : 'items-center'} ${hasNameVerticalStretch || (layoutStyle?.unit?.verticalAlignment && layoutStyle?.unit?.verticalAlignment !== 'bottom') ? 'h-full' : ''} mr-4 overflow-hidden flex-1`}
          >
            <h3
              ref={nameRef}
              className="font-bold text-3xl text-gray-800 leading-none"
              style={{
                ...getTextStyle('productName'),
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                display: hasNameVerticalStretch ? 'flex' : 'block',
                alignItems: hasNameVerticalStretch ? 'center' : undefined,
              }}
            >
              {truncatedName}
            </h3>
            {/* Unidade ao lado direito do nome, bem próxima e alinhada verticalmente */}
            <span
              ref={unitRef}
              className="text-gray-500 ml-1"
              style={{
                ...getTextStyle('unit'),
                // Aplicar alinhamento vertical ou offset se configurado
                alignSelf: (() => {
                  const unitStyle = layoutStyle?.unit;
                  const verticalAlignment = unitStyle?.verticalAlignment;
                  
                  // Quando 'bottom', alinhar na linha de base inferior do texto (baseline)
                  // Isso faz o "UN" ficar alinhado na parte inferior do texto do produto
                  if (verticalAlignment === 'bottom') return 'baseline';
                  // 'center' ou undefined = centralizado (padrão)
                  return 'center';
                })(),
                transform: (() => {
                  const unitStyle = layoutStyle?.unit;
                  const verticalOffset = unitStyle?.verticalOffset; // Para compatibilidade com versões antigas
                  const verticalStretch = unitStyle?.verticalStretch || 100;
                  const hasVerticalStretch = verticalStretch > 100;
                  
                  // Se houver verticalAlignment, não usar offset em pixels
                  if (unitStyle?.verticalAlignment) {
                    // Apenas aplicar stretch se necessário
                    return hasVerticalStretch ? `scaleY(${verticalStretch / 100})` : undefined;
                  }
                  
                  // Compatibilidade: usar offset em pixels se não houver alignment
                  if (verticalOffset !== undefined && verticalOffset !== 0) {
                    if (hasVerticalStretch) {
                      return `translateY(${verticalOffset}px) scaleY(${verticalStretch / 100})`;
                    }
                    return `translateY(${verticalOffset}px)`;
                  }
                  
                  return hasVerticalStretch ? `scaleY(${verticalStretch / 100})` : undefined;
                })(),
                transformOrigin: 'center',
              }}
            >
              {promo.unit}
            </span>
          </div>
          <div className="flex items-center whitespace-nowrap ml-4">
            <div
              style={(() => {
                const pb = layoutStyle?.priceBox;
                if (!pb || pb.enabled === false) return undefined;
                const style: React.CSSProperties = {
                  display: 'inline-block',
                  backgroundColor: (() => {
                    const bgColor = pb.backgroundColor || '#ffffff';
                    const opacityPercent = layoutStyle?.productContainer?.backgroundImageOpacity ?? 100;
                    const alpha = Math.max(0, Math.min(1, opacityPercent / 100));
                    return alpha < 1 ? hexToRgba(bgColor, alpha) : bgColor;
                  })(),
                  borderRadius: pb.borderRadius != null ? pb.borderRadius : 8,
                  paddingTop: pb.paddingTop != null ? pb.paddingTop : 4,
                  paddingRight: pb.paddingRight != null ? pb.paddingRight : 8,
                  paddingBottom: pb.paddingBottom != null ? pb.paddingBottom : 4,
                  paddingLeft: pb.paddingLeft != null ? pb.paddingLeft : 8,
                  marginTop: pb.marginTop != null ? pb.marginTop : 0,
                  marginRight: pb.marginRight != null ? pb.marginRight : 0,
                  marginBottom: pb.marginBottom != null ? pb.marginBottom : 0,
                  marginLeft: pb.marginLeft != null ? pb.marginLeft : 0,
                  overflow: 'hidden',
                };
                const bw = pb.borderWidth != null ? pb.borderWidth : 0;
                if (bw > 0) {
                  const borderBaseColor = pb.borderColor || '#000000';
                  const opacityPercent = layoutStyle?.productContainer?.backgroundImageOpacity ?? 100;
                  const alpha = Math.max(0, Math.min(1, opacityPercent / 100));
                  const borderColor = alpha < 1 ? hexToRgba(borderBaseColor, alpha) : borderBaseColor;
                  style.border = `${bw}px solid ${borderColor}`;
                }
                (style as any).borderRadius = `${style.borderRadius}px`;
                return style;
              })()}
            >
              <span
                className="text-4xl font-bold text-green-800"
                style={getTextStyle('price')}
              >
                {formatBrazilianNumber(promo.price, layoutStyle?.price?.showCurrency !== false)}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}; 